/**
 * Newsletter Popup - New Design
 * Features:
 * - Shows once per day
 * - Configurable delay (default 5 seconds)
 * - Floating tab when closed
 * - Session/localStorage persistence
 */

class NewsletterPopup {
  constructor(element) {
    this.popup = element;
    this.enabled = this.popup.dataset.enabled === 'true';
    this.delay = parseInt(this.popup.dataset.delay || '5', 10) * 1000;
    this.cookieName = 'newsletter-popup-dismissed';
    this.sessionKey = 'newsletter-popup-shown-today';
    this.isEditorMode = window.Shopify && window.Shopify.designMode;
    
    this.popupElement = this.popup.querySelector('.newsletter-popup');
    this.tabElement = this.popup.querySelector('.newsletter-tab');
    this.form = this.popup.querySelector('.newsletter-popup__form');
    
    if (!this.enabled) {
      return;
    }
    
    this.init();
  }
  
  init() {
    // Bind events first
    this.bindEvents();
    
    // In editor mode, check if already shown in this session
    if (this.isEditorMode) {
      const editorSessionKey = 'newsletter-popup-editor-shown';
      const alreadyShownInEditor = sessionStorage.getItem(editorSessionKey);
      
      if (alreadyShownInEditor) {
        return;
      }
      
      sessionStorage.setItem(editorSessionKey, 'true');
      setTimeout(() => this.showPopup(), 100);
      return;
    }
    
    // Check if permanently dismissed
    if (this.isPermanentlyDismissed()) {
      return;
    }
    
    // Check if already shown today
    if (this.wasShownToday()) {
      this.showTab();
      return;
    }
    
    // Show popup after delay
    setTimeout(() => {
      this.showPopup();
      this.markShownToday();
    }, this.delay);
  }
  
  bindEvents() {
    // Close popup button
    const closeBtn = this.popup.querySelector('.newsletter-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup());
    }
    
    // Overlay click
    const overlay = this.popup.querySelector('.newsletter-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup());
    }
    
    // Form submission - ONLY for the popup form (ID: newsletter-popup-form)
    if (this.form && this.form.id === 'newsletter-popup-form') {
      // Prevent form default submission for popup form only
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }, true);
      
      // Handle submission via button click
      const submitBtn = this.form.querySelector('.newsletter-popup__submit');
      if (submitBtn) {
        submitBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleSubmit(e);
          return false;
        });
      }
    }
    
    // Tab click to reopen
    const tabContent = this.popup.querySelector('.newsletter-tab__content');
    if (tabContent) {
      tabContent.addEventListener('click', () => this.showPopup());
    }
    
    // Tab close button
    const tabClose = this.popup.querySelector('.newsletter-tab__close');
    if (tabClose) {
      tabClose.addEventListener('click', (e) => {
        e.stopPropagation();
        this.permanentlyDismiss();
      });
    }
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popupElement.classList.contains('is-visible')) {
        this.closePopup();
      }
    });
  }
  
  showPopup() {
    if (this.popupElement) {
      // Lazy-load background image only when popup is about to show,
      // so it doesn't compete with LCP images during page load.
      const container = this.popupElement.querySelector('[data-bg]');
      if (container && !container.style.backgroundImage) {
        container.style.backgroundImage = `url('${container.dataset.bg}')`;
      }
      this.popupElement.classList.add('is-visible');
      this.hideTab();
      document.body.style.overflow = 'hidden';
    }
  }
  
  closePopup() {
    if (this.popupElement) {
      this.popupElement.classList.remove('is-visible');
      document.body.style.overflow = '';
      this.showTab();
    }
  }
  
  showTab() {
    if (this.tabElement) {
      this.tabElement.style.display = 'block';
      setTimeout(() => {
        this.tabElement.classList.add('is-visible');
      }, 100);
    }
  }
  
  hideTab() {
    if (this.tabElement) {
      this.tabElement.classList.remove('is-visible');
      setTimeout(() => {
        this.tabElement.style.display = 'none';
      }, 300);
    }
  }
  
  permanentlyDismiss() {
    this.setCookie(this.cookieName, 'true', 365);
    this.hideTab();
  }
  
  wasShownToday() {
    const today = new Date().toDateString();
    const lastShown = sessionStorage.getItem(this.sessionKey);
    return lastShown === today;
  }
  
  markShownToday() {
    const today = new Date().toDateString();
    sessionStorage.setItem(this.sessionKey, today);
  }
  
  isPermanentlyDismissed() {
    return this.getCookie(this.cookieName) === 'true';
  }
  
  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
  }
  
  getCookie(name) {
    const cookieString = `; ${document.cookie}`;
    const cookies = cookieString.split(`; ${name}=`);
    if (cookies.length === 2) {
      return decodeURIComponent(cookies.pop().split(';').shift());
    }
    return null;
  }
  
  async handleSubmit(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const emailInput = this.form.querySelector('.newsletter-popup__input') || this.form.querySelector('input[type="email"]');
    if (!emailInput) {
      return false;
    }
    
    const email = emailInput.value.trim();
    
    if (!email || !this.validateEmail(email)) {
      this.showErrorMessage('Please enter a valid email address');
      return false;
    }
    
    const submitBtn = this.form.querySelector('.newsletter-popup__submit');
    const originalHTML = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<svg style="animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"></circle><path d="M12 2a10 10 0 0 1 10 10" opacity="0.75"></path></svg><style>@keyframes spin { to { transform: rotate(360deg); } }</style>';
    
    try {
      // Use iframe submission to let Shopify handle CAPTCHA
      await this.submitViaIframe();
      
      // Success
      this.showSuccessMessage('Thank you for subscribing! ✓');
      emailInput.value = '';
      
      // Close popup after 2 seconds
      setTimeout(() => {
        this.closePopup();
      }, 2000);
      
      // Track conversion if analytics available
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'newsletter_signup', {
          'event_category': 'engagement',
          'event_label': 'popup'
        });
      }
    } catch (error) {
      this.showErrorMessage('Something went wrong. Please try again.');
    } finally {
      setTimeout(() => {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalHTML;
      }, 500);
    }
    
    return false;
  }
  
  submitViaIframe() {
    return new Promise((resolve, reject) => {
      const iframeName = 'newsletter-iframe-' + Date.now();
      const iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;display:none;';
      document.body.appendChild(iframe);
      
      // Store original target
      const originalTarget = this.form.target;
      const originalAction = this.form.action;
      
      // Set form to submit to iframe
      this.form.target = iframeName;
      
      // Listen for iframe load
      let hasResolved = false;
      const timeout = setTimeout(() => {
        if (!hasResolved) {
          hasResolved = true;
          cleanup();
          resolve(); // Assume success after timeout
        }
      }, 2000);
      
      iframe.onload = () => {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeout);
          setTimeout(() => {
            cleanup();
            resolve();
          }, 100);
        }
      };
      
      const cleanup = () => {
        // Restore original target
        this.form.target = originalTarget || '';
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };
      
      // Submit the actual form (Shopify will handle CAPTCHA)
      try {
        this.form.submit();
      } catch (error) {
        clearTimeout(timeout);
        cleanup();
        reject(error);
      }
    });
  }
  
  showSuccessMessage(text) {
    // Remove any existing messages first
    const existingMsg = document.querySelector('.newsletter-popup-message');
    if (existingMsg) existingMsg.remove();
    
    // Create success message
    const message = document.createElement('div');
    message.className = 'newsletter-popup-message newsletter-popup-message--success';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #4caf50;
      color: white;
      padding: 16px 32px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      z-index: 10001;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideDown 0.3s ease-out;
    `;
    message.textContent = text;
    document.body.appendChild(message);
    
    // Add animation styles
    if (!document.querySelector('#newsletter-popup-animations')) {
      const style = document.createElement('style');
      style.id = 'newsletter-popup-animations';
      style.textContent = '@keyframes slideDown { from { transform: translateX(-50%) translateY(-20px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }';
      document.head.appendChild(style);
    }
    
    setTimeout(() => {
      message.style.opacity = '0';
      message.style.transition = 'opacity 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }
  
  showErrorMessage(text) {
    // Remove any existing messages first
    const existingMsg = document.querySelector('.newsletter-popup-message');
    if (existingMsg) existingMsg.remove();
    
    // Create error message
    const message = document.createElement('div');
    message.className = 'newsletter-popup-message newsletter-popup-message--error';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #f44336;
      color: white;
      padding: 16px 32px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      z-index: 10001;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideDown 0.3s ease-out;
    `;
    message.textContent = text;
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.opacity = '0';
      message.style.transition = 'opacity 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }
  
  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }
  
  // Debug helper - call NewsletterPopup.reset() in console to clear all data
  static reset() {
    sessionStorage.removeItem('newsletter-popup-shown-today');
    document.cookie = 'newsletter-popup-dismissed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
}

// Make reset function globally accessible for debugging
window.NewsletterPopup = NewsletterPopup;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNewsletterPopup);
} else {
  initNewsletterPopup();
}

function initNewsletterPopup() {
  const popupElement = document.querySelector('[data-newsletter-popup]');
  if (popupElement) {
    const instance = new NewsletterPopup(popupElement);
    
    // Check if we just submitted the form (in case JS didn't prevent default)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('customer_posted') === 'true') {
      // Clean the URL
      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.pathname + window.location.hash.replace('#newsletter-popup-form', '');
        window.history.replaceState({}, document.title, cleanUrl);
      }
      // Show success message
      if (instance.showSuccessMessage) {
        instance.showSuccessMessage('Thank you for subscribing! ✓');
      }
    }
  }
}
